import { useContext, createContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { isEqual, uniqueId } from "lodash";

import { useFieldErrors } from "./useFieldErrors";
import ErrorList from "./ErrorList";
import { useForm } from "./withFormStore";
import { useUniqueId } from "../Utils/uniqueId";

import { updateFieldSchema } from "../../reducers/form/schema";

export const FIELD_MODE_LABEL = "LABEL";
export const FIELD_MODE_FIELDSET = "FIELDSET";
export const FIELD_MODE_NO_FIELD = "NOFIELD";

const FieldContext = createContext({ fieldId: null });

export const useFieldContext = () => useContext(FieldContext) || {};

const Field = ({ fieldMode = FIELD_MODE_LABEL, ...props }) => {
  const {
    fieldName,
    required,
    hint,
    hintPosition,
    hide,
    label,
    labelRef,
    schema,
    children
  } = props;
  const { isInvalid, errors, showErrors } = useFieldErrors(fieldName);
  const { formKey } = useForm();

  const dispatch = useDispatch();

  // This is used a schema cache to avoid useless schema updates. It allows us to use
  // lodash's `isEqual` function to make a deep comparison between existing `schema` value
  // and the current `schema` that's fetched from the props.
  const previousSchemaRef = useRef();

  // Update the field's form schema in the redux store when the component is mounted, and
  // if the schema is changed during the runtime.
  useEffect(() => {
    if (isEqual(schema, previousSchemaRef.current)) return;

    previousSchemaRef.current = schema;
    dispatch(updateFieldSchema(formKey, fieldName, schema));
  }, [formKey, fieldName, schema, dispatch]);

  // Those are DOM ids used for the different parts of the field to make them accessible
  // through different HTML and ARIA attributes.
  const [fieldId, setFieldId] = useState(uniqueId("field"));
  const fieldErrorId = useUniqueId("field-error-message");
  const fieldHintId = useUniqueId("field-hint");

  const inputNode = useMemo(
    () => <div className="form-controls">{children}</div>,
    [children]
  );

  const hintNode = useMemo(() => {
    if (!hint) return null;

    const hintClassName = classNames("form-text", {
      "form-hint-above": hintPosition === "ABOVE_FIELD",
      "form-hint-below": hintPosition !== "ABOVE_FIELD"
    });

    return (
      <p id={fieldHintId} className={hintClassName}>
        {hint}
      </p>
    );
  }, [fieldHintId, hint, hintPosition]);

  // This returns the input node and the hint of the field disposed in the way it's
  // configured to be, depending on the presence of a hint and the hintPosition prop.
  const inputWithHint = useMemo(() => {
    if (!hint) {
      // When no hint is provided, just render the input node
      return inputNode;
    } else if (hintPosition === "ABOVE_FIELD") {
      // When the hint must be displayed above the input
      return (
        <>
          {hintNode}
          {inputNode}
        </>
      );
    } else {
      // When the hint must be displayed below the input
      return (
        <>
          {inputNode}
          {hintNode}
        </>
      );
    }
  }, [hint, hintNode, hintPosition, inputNode]);

  // This returns the inputNode, the hint and the errors list, which are the inner part
  // of the field, except from the <label> or <legend> tag the labels it.
  const fieldContent = useMemo(
    () => (
      <>
        {inputWithHint}
        <ErrorList errors={errors} showErrors={showErrors} isInvalid={isInvalid} />
      </>
    ),
    [errors, inputWithHint, isInvalid, showErrors]
  );

  const labelContent = useMemo(() => {
    if (!label) return null;

    return (
      <>
        {required && <b className="margin-right-xxs">*</b>}
        {label}
      </>
    );
  }, [label, required]);

  // This is the context value that the field provides to its child components
  const valueProvider = {
    fieldId,
    fieldName,
    setFieldId,
    isRequired: required,
    isInvalid,
    fieldErrorId: isInvalid && fieldErrorId,
    fieldHintId: hint && fieldHintId
  };

  const className = classNames("form-group", props.className, {
    "form-group-invalid": isInvalid,
    hidden: hide
  });

  const [ContainerTagName, LabelTagName, htmlFor] = useMemo(() => {
    switch (fieldMode) {
      case FIELD_MODE_LABEL:
        return ["div", "label", fieldId];
      case FIELD_MODE_FIELDSET:
        return ["fieldset", "legend", null];
      case FIELD_MODE_NO_FIELD:
        return ["div", "span", null];
    }
  }, [fieldId, fieldMode]);

  return (
    <FieldContext.Provider value={valueProvider}>
      <ContainerTagName className={className} data-form-field={fieldName}>
        {labelContent && (
          <LabelTagName ref={labelRef} htmlFor={htmlFor} className="form-label">
            {labelContent}
          </LabelTagName>
        )}
        {fieldContent}
      </ContainerTagName>
    </FieldContext.Provider>
  );
};

export default Field;
